import { useState, useEffect, useRef } from 'react';
import { Search, Package, Wrench, X } from 'lucide-react';
import { ProdutoServico } from '../services/propostaService';

interface ProdutoServicoSelectorProps {
  produtos: ProdutoServico[];
  onSelect: (produto: ProdutoServico) => void;
  onClose: () => void;
  tipoFiltroInicial?: 'todos' | 'produto' | 'servico';
}

const MAX_RESULTS = 15;

export function ProdutoServicoSelector({ produtos, onSelect, onClose, tipoFiltroInicial = 'todos' }: ProdutoServicoSelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState<'todos' | 'produto' | 'servico'>(tipoFiltroInicial);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  // Filtrar produtos
  const filteredProdutos = produtos.filter(p => {
    // Filtro por tipo
    if (tipoFiltro !== 'todos' && p.tipo !== tipoFiltro) return false;

    // Filtro por busca (se tiver termo)
    if (searchTerm.length >= 2) {
      const term = searchTerm.toLowerCase();
      return (
        p.nome.toLowerCase().includes(term) ||
        (p.codigo && p.codigo.toLowerCase().includes(term)) ||
        (p.descricao && p.descricao.toLowerCase().includes(term))
      );
    }

    return true;
  });

  // Limitar resultados
  const displayProdutos = filteredProdutos.slice(0, MAX_RESULTS);
  const hasMore = filteredProdutos.length > MAX_RESULTS;

  // Contadores por tipo
  const countProdutos = produtos.filter(p => p.tipo === 'produto').length;
  const countServicos = produtos.filter(p => p.tipo === 'servico').length;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Backdrop semitransparente */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Painel do seletor */}
      <div className="relative w-full max-w-lg bg-card border border-input rounded-xl shadow-2xl flex flex-col max-h-[80vh]">
        {/* Header com busca */}
        <div className="p-3 border-b bg-muted/50 rounded-t-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-foreground">Selecionar Produto / Serviço</span>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-muted">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar por nome, código ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-input rounded-lg text-sm bg-card text-foreground focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Abas de filtro por tipo */}
          <div className="flex gap-1 mt-2">
            <button
              type="button"
              onClick={() => setTipoFiltro('todos')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${tipoFiltro === 'todos'
                ? 'bg-brand text-brand-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
            >
              Todos ({produtos.length})
            </button>
            <button
              type="button"
              onClick={() => setTipoFiltro('produto')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${tipoFiltro === 'produto'
                ? 'bg-purple-600 text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
            >
              <Package className="w-3 h-3" /> Produtos ({countProdutos})
            </button>
            <button
              type="button"
              onClick={() => setTipoFiltro('servico')}
              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1 ${tipoFiltro === 'servico'
                ? 'bg-brand text-brand-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
            >
              <Wrench className="w-3 h-3" /> Serviços ({countServicos})
            </button>
          </div>
        </div>

        {/* Lista de resultados */}
        <div className="flex-1 overflow-y-auto">
          {displayProdutos.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              {searchTerm.length >= 2
                ? 'Nenhum item encontrado para esta busca'
                : searchTerm.length > 0
                  ? 'Digite pelo menos 2 caracteres para buscar'
                  : 'Nenhum item cadastrado'
              }
            </div>
          ) : (
            <>
              {displayProdutos.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelect(p)}
                  className="w-full px-4 py-3 text-left hover:bg-muted/50 flex items-start gap-3 border-b last:border-b-0 transition-colors"
                >
                  <div className={`mt-0.5 p-1.5 rounded ${p.tipo === 'produto' ? 'bg-purple-100' : 'bg-cyan-100'}`}>
                    {p.tipo === 'produto'
                      ? <Package className="w-4 h-4 text-purple-600" />
                      : <Wrench className="w-4 h-4 text-cyan-600" />
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {p.codigo && (
                        <span className="text-xs font-mono text-muted-foreground">[{p.codigo}]</span>
                      )}
                      <span className="font-medium text-foreground truncate">{p.nome}</span>
                    </div>
                    {p.descricao && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{p.descricao}</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    {Number(p.valorUnit) > 0 ? (
                      <>
                        <span className="font-semibold text-foreground">{formatCurrency(Number(p.valorUnit))}</span>
                        <span className="text-xs text-muted-foreground">/{p.unidade}</span>
                      </>
                    ) : p.precoMinimo ? (
                      <>
                        <span className="font-semibold text-foreground">{formatCurrency(Number(p.precoMinimo))}</span>
                        <span className="text-xs text-muted-foreground">/{p.unidade}</span>
                        <div className="text-xs text-amber-600 dark:text-amber-400">preço mín.</div>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">variável</span>
                    )}
                  </div>
                </button>
              ))}

              {hasMore && (
                <div className="px-4 py-2 text-center text-xs text-muted-foreground bg-muted/50 border-t">
                  Mostrando {MAX_RESULTS} de {filteredProdutos.length} resultados.{' '}
                  <span className="text-blue-600">Refine sua busca para ver mais.</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-2 border-t bg-muted/50 flex justify-end rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
